import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { supabase, getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';
import type {
  TeamMember,
  CreateTeamMemberInput,
  UpdateTeamMemberInput,
} from '@/types/database';

// ============================================
// Query Keys
// ============================================
const TEAM_MEMBERS_KEY = 'teamMembers';

// ============================================
// Fetch Team Members
// ============================================
export function useTeamMembers() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: [TEAM_MEMBERS_KEY, userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await getSupabase()
        .from('team_members')
        .select('*')
        .eq('landlord_id', userId)
        .order('is_primary', { ascending: false })
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching team members:', error);
        throw error;
      }

      return data as TeamMember[];
    },
    enabled: isReady,
  });
}

// ============================================
// Fetch Team Members for Guest (by landlord_id)
// ============================================
export function useTeamMembersForGuest(landlordId: string | undefined) {
  return useQuery({
    queryKey: [TEAM_MEMBERS_KEY, 'guest', landlordId],
    queryFn: async () => {
      if (!landlordId) return [];

      const { data, error } = await supabase.rpc('get_team_members_for_guest', {
        p_landlord_id: landlordId,
      });

      if (error) {
        console.error('Error fetching team members for guest:', error);
        return [];
      }

      return data as TeamMember[];
    },
    enabled: !!landlordId,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================
// Get Primary Team Member
// ============================================
export function usePrimaryTeamMember(landlordId: string | undefined) {
  const { data: teamMembers } = useTeamMembersForGuest(landlordId);
  return teamMembers?.find((m) => m.is_primary) || teamMembers?.[0] || null;
}

// ============================================
// Get Emergency Contact
// ============================================
export function useEmergencyContact(landlordId: string | undefined) {
  const { data: teamMembers } = useTeamMembersForGuest(landlordId);
  return teamMembers?.find((m) => m.is_emergency) || null;
}

// ============================================
// Create Team Member
// ============================================
export function useCreateTeamMember() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async (input: CreateTeamMemberInput) => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await getSupabase()
        .from('team_members')
        .insert({
          landlord_id: user.id,
          ...input,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating team member:', error);
        throw error;
      }

      return data as TeamMember;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_KEY] });
    },
  });
}

// ============================================
// Update Team Member
// ============================================
export function useUpdateTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateTeamMemberInput;
    }) => {
      const { data, error } = await getSupabase()
        .from('team_members')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating team member:', error);
        throw error;
      }

      return data as TeamMember;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_KEY] });
    },
  });
}

// ============================================
// Delete Team Member
// ============================================
export function useDeleteTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await getSupabase().from('team_members').delete().eq('id', id);

      if (error) {
        console.error('Error deleting team member:', error);
        throw error;
      }

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_KEY] });
    },
  });
}

// ============================================
// Set Primary Team Member
// ============================================
export function useSetPrimaryTeamMember() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async (memberId: string) => {
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await getSupabase().rpc('set_primary_team_member', {
        p_member_id: memberId,
        p_landlord_id: user.id,
      });

      if (error) {
        console.error('Error setting primary team member:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_KEY] });
    },
  });
}

// ============================================
// Reorder Team Members
// ============================================
export function useReorderTeamMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update display_order for each member
      const updates = orderedIds.map((id, index) =>
        getSupabase()
          .from('team_members')
          .update({ display_order: index })
          .eq('id', id)
      );

      await Promise.all(updates);

      return orderedIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_KEY] });
    },
  });
}

// ============================================
// Calculate Response Time
// ============================================
export function useLandlordResponseTime(landlordId: string | undefined) {
  return useQuery({
    queryKey: ['responseTime', landlordId],
    queryFn: async () => {
      if (!landlordId) return null;

      const { data, error } = await supabase.rpc('calculate_landlord_response_time', {
        p_landlord_id: landlordId,
      });

      if (error) {
        console.error('Error calculating response time:', error);
        return 30; // Default 30 minutes
      }

      return data as number;
    },
    enabled: !!landlordId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

// ============================================
// Helper: Format response time for display
// ============================================
export function formatResponseTime(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '';

  if (minutes < 60) {
    return `~${minutes} min`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `~${hours}h`;
  }

  const days = Math.round(hours / 24);
  return `~${days}d`;
}
